import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Home,
  CheckCircle,
  Clock,
  DollarSign,
  Shield,
  AlertTriangle,
  ArrowRight,
  Phone,
  FileText,
  Layers,
  ThermometerSun,
  Droplets,
  Wind,
  Calendar,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadForm from "@/components/LeadForm";
import useSEO from "@/hooks/useSEO";

const FAQ_DATA = [
  {
    question: "How much does a roof replacement cost in Minnesota?",
    answer: "Roof replacement costs in Minnesota typically range from $8,000 to $25,000+ depending on roof size, pitch, material choice, and complexity. Insurance claims for storm damage can significantly reduce out-of-pocket costs. A professional assessment provides accurate pricing for your specific situation."
  },
  {
    question: "How long does a roof replacement take?",
    answer: "Most residential roof replacements in Minnesota take 1-3 days for standard homes. Larger homes, complex roof designs, or weather delays may extend this timeline. Your contractor should provide a specific timeline based on your project scope."
  },
  {
    question: "What are signs I need a new roof vs. repairs?",
    answer: "Consider replacement if your roof is 20+ years old, has widespread damage, shows multiple leaks, has significant granule loss, or repair costs exceed 30% of replacement cost. A professional inspection can determine the most cost-effective solution."
  },
  {
    question: "Does insurance cover roof replacement?",
    answer: "Homeowners insurance typically covers roof replacement when damage results from covered perils like hail, wind, or fallen trees. Age-related wear is usually not covered. Filing a claim promptly after storm damage is important for coverage."
  },
  {
    question: "What roofing materials are best for Minnesota?",
    answer: "Asphalt architectural shingles are most popular due to durability and cost-effectiveness. Impact-resistant shingles offer better hail protection. Metal roofing provides excellent longevity. Your choice depends on budget, aesthetic preferences, and long-term plans."
  },
  {
    question: "Can I stay in my home during roof replacement?",
    answer: "Yes, most homeowners stay in their homes during roof replacement. Expect noise during work hours and some disruption. Contractors typically protect your property and clean up daily. Discuss any concerns with your contractor beforehand."
  },
  {
    question: "What warranty should I expect with a new roof?",
    answer: "Quality roof replacements include both manufacturer warranties (25-50 years on materials) and workmanship warranties from the contractor (typically 5-10 years). Understand what each covers before signing contracts."
  },
  {
    question: "How do I choose the right roofing contractor?",
    answer: "Verify Minnesota licensing, check insurance certificates, read reviews, ask for local references, get detailed written estimates, and avoid storm chasers who pressure immediate decisions. Local contractors with established reputations are typically most reliable."
  }
];

const ROOFING_MATERIALS = [
  {
    name: "Asphalt Architectural Shingles",
    lifespan: "25-30 years",
    pros: ["Cost-effective", "Wide color selection", "Easy installation"],
    cons: ["Moderate hail resistance", "Shorter lifespan than premium options"],
    bestFor: "Budget-conscious homeowners wanting reliable performance"
  },
  {
    name: "Impact-Resistant Shingles",
    lifespan: "30-50 years",
    pros: ["Superior hail protection", "Insurance discounts available", "Longer warranty"],
    cons: ["Higher upfront cost", "Limited style options"],
    bestFor: "Minnesota homes in hail-prone areas"
  },
  {
    name: "Metal Roofing",
    lifespan: "40-70 years",
    pros: ["Exceptional durability", "Energy efficient", "Low maintenance"],
    cons: ["Higher initial cost", "Requires specialized installation"],
    bestFor: "Homeowners planning to stay long-term"
  },
  {
    name: "Designer/Luxury Shingles",
    lifespan: "30-50 years",
    pros: ["Premium aesthetics", "Enhanced curb appeal", "Superior performance"],
    cons: ["Highest cost option", "Longer installation time"],
    bestFor: "Upscale homes prioritizing appearance and value"
  }
];

const WARNING_SIGNS = [
  { icon: Calendar, title: "Age Over 20 Years", description: "Minnesota roofs typically last 20-25 years. Older roofs become increasingly vulnerable to damage." },
  { icon: Droplets, title: "Multiple Leaks", description: "Recurring leaks in different areas indicate systemic roof failure rather than isolated damage." },
  { icon: Layers, title: "Widespread Granule Loss", description: "Excessive granules in gutters signal shingle deterioration and reduced protection." },
  { icon: Wind, title: "Missing/Damaged Shingles", description: "Multiple missing shingles or widespread cracking suggests replacement may be more cost-effective than repairs." },
  { icon: ThermometerSun, title: "Rising Energy Bills", description: "Poor roof ventilation or insulation can indicate underlying issues requiring replacement." },
  { icon: AlertTriangle, title: "Sagging Roof Deck", description: "Any visible sagging indicates structural issues requiring immediate professional assessment." }
];

export default function RoofReplacement() {
  useSEO();
  const [openFaq, setOpenFaq] = useState(null);

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Roof Replacement Guide for Minnesota Homeowners",
    "description": "Complete guide to roof replacement in Minnesota including costs, materials, timeline, and how to choose a contractor.",
    "author": {
      "@type": "Organization",
      "name": "StormCareMN"
    },
    "publisher": {
      "@type": "Organization",
      "name": "StormCareMN",
      "url": "https://stormcaremn.com"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div data-testid="roof-replacement-page" className="min-h-screen bg-white">
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero Section */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-gray-400 text-sm mb-6">
                <Link to="/" className="hover:text-orange">Home</Link>
                <span>/</span>
                <span className="text-gray-300">Roof Replacement</span>
              </nav>

              <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
                <Home className="w-4 h-4" />
                Educational Resource
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white tracking-tight leading-tight mb-6">
                Roof Replacement in Minnesota:<br />
                <span className="text-orange">What Homeowners Need to Know</span>
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Whether storm damage or age is driving your decision, understanding the roof replacement 
                process helps you make informed choices. This guide covers costs, materials, timelines, 
                and how to find the right contractor.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-orange hover:bg-orange-600 text-white">
                  <a href="#get-started">
                    <Phone className="w-4 h-4 mr-2" />
                    Get Free Assessment
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-gray-600 text-white hover:bg-white/10">
                  <a href="#warning-signs">
                    Learn Warning Signs
                  </a>
                </Button>
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1632759145351-1d592919f522?w=600"
                alt="Professional roof replacement in Minnesota"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs Section */}
      <section id="warning-signs" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Signs You May Need Roof Replacement
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Not sure if you need a full replacement or just repairs? These warning signs help you understand 
              when replacement becomes the more practical choice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WARNING_SIGNS.map((sign, idx) => (
              <div key={`warning-${idx}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-orange/30 transition-colors">
                <div className="w-12 h-12 bg-orange/10 rounded-lg flex items-center justify-center mb-4">
                  <sign.icon className="w-6 h-6 text-orange" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{sign.title}</h3>
                <p className="text-gray-600 text-sm">{sign.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Roofing Materials for Minnesota Homes
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Minnesota weather demands durable roofing materials. Compare your options to find the best fit 
              for your home, budget, and long-term plans.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {ROOFING_MATERIALS.map((material, idx) => (
              <div key={`material-${idx}`} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-xl font-semibold text-navy">{material.name}</h3>
                  <span className="bg-navy text-white text-xs px-3 py-1 rounded-full">{material.lifespan}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-green-700 mb-2">Advantages</h4>
                    <ul className="space-y-1">
                      {material.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-amber-700 mb-2">Considerations</h4>
                    <ul className="space-y-1">
                      {material.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <span className="text-sm text-gray-500">Best for: </span>
                  <span className="text-sm text-navy font-medium">{material.bestFor}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-white mb-4">
              The Roof Replacement Process
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Understanding each step helps you know what to expect and ensures a smooth project experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Inspection & Estimate", desc: "Professional assessment of your roof condition, measurements, and detailed written estimate.", time: "1-2 days" },
              { step: "2", title: "Material Selection", desc: "Choose shingle style, color, and any upgrades. Materials are ordered and scheduled for delivery.", time: "1-2 weeks" },
              { step: "3", title: "Installation Day(s)", desc: "Old roof removal, deck inspection/repair, new underlayment and shingles installed.", time: "1-3 days" },
              { step: "4", title: "Final Inspection", desc: "Walkthrough to verify quality, cleanup confirmation, warranty documentation provided.", time: "Same day" }
            ].map((phase, idx) => (
              <div key={`phase-${idx}`} className="relative">
                <div className="bg-white/10 rounded-xl p-6 h-full">
                  <div className="w-10 h-10 bg-orange rounded-full flex items-center justify-center text-white font-bold mb-4">
                    {phase.step}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">{phase.title}</h3>
                  <p className="text-gray-300 text-sm mb-3">{phase.desc}</p>
                  <div className="flex items-center gap-2 text-orange text-sm">
                    <Clock className="w-4 h-4" />
                    {phase.time}
                  </div>
                </div>
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-orange" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Common questions Minnesota homeowners ask about roof replacement.
            </p>
          </div>

          <div className="space-y-4">
            {FAQ_DATA.map((faq, idx) => (
              <div 
                key={`faq-${idx}`}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-heading font-semibold text-navy pr-4">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-orange flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-started" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
                Ready to Explore Your Options?
              </h2>
              <p className="text-gray-600 mb-6">
                Whether you have storm damage or an aging roof, getting a professional assessment is the 
                first step. Connect with trusted Minnesota roofing professionals who can evaluate your 
                situation and provide honest guidance.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">Free professional roof inspection</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">Detailed assessment and recommendations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">Insurance claim assistance if applicable</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">No pressure, no obligation</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/service-areas" className="text-orange hover:text-orange-600 font-medium flex items-center gap-2">
                  Find your city <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/blog" className="text-navy hover:text-orange font-medium flex items-center gap-2">
                  Read more articles <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="font-heading text-xl font-semibold text-navy mb-2 text-center">
                Get Your Free Assessment
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Tell us about your situation
              </p>
              <LeadForm compact={true} />
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h3 className="font-heading text-lg font-semibold text-navy mb-6">Related Resources</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/storm-damage-restoration" className="bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Storm Damage Restoration Guide
            </Link>
            <Link to="/blog/roof-replacement-vs-repair-when-to-choose" className="bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Repair vs. Replace Decision Guide
            </Link>
            <Link to="/blog/how-to-choose-roofing-contractor-minnesota" className="bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Choosing a Contractor
            </Link>
            <Link to="/faqs" className="bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              More FAQs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
